import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Plus, User, AlertTriangle, CheckCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLanguage } from "@/contexts/LanguageContext";

interface BehaviorType {
  id: string;
  name: string;
  name_el?: string;
  name_en?: string;
  name_fr?: string;
  category: 'positive' | 'negative';
  score_impact: number;
  description?: string;
  description_el?: string;
  description_en?: string;
  description_fr?: string;
  color?: string;
  icon?: string;
}

interface Guard {
  id: string;
  first_name: string;
  last_name: string;
  full_name?: string;
}

interface Team {
  id: string;
  name: string;
}

const behaviorSchema = z.object({
  guard_id: z.string().min(1, "Παρακαλώ επιλέξτε φύλακα"),
  team_id: z.string().min(1, "Παρακαλώ επιλέξτε ομάδα"),
  behavior_type_id: z.string().min(1, "Παρακαλώ επιλέξτε τύπο συμπεριφοράς"),
  incident_date: z.string().min(1, "Παρακαλώ επιλέξτε ημερομηνία"),
  description: z.string().optional(),
  evidence: z.string().optional(),
});

type BehaviorFormData = z.infer<typeof behaviorSchema>;

interface AddBehaviorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddBehaviorModal = ({ open, onOpenChange }: AddBehaviorModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { language } = useLanguage();

  const getLocalizedName = (type: BehaviorType) => {
    if (language === 'el') return type.name_el || type.name;
    if (language === 'en') return type.name_en || type.name;
    if (language === 'fr') return type.name_fr || type.name;
    return type.name;
  };

  const getLocalizedDescription = (type: BehaviorType) => {
    if (language === 'el') return type.description_el || type.description;
    if (language === 'en') return type.description_en || type.description;
    if (language === 'fr') return type.description_fr || type.description;
    return type.description;
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<BehaviorFormData>({
    resolver: zodResolver(behaviorSchema),
    defaultValues: {
      incident_date: new Date().toISOString().split('T')[0],
    },
  });

  const selectedTeamId = watch('team_id');
  const selectedBehaviorTypeId = watch('behavior_type_id');

  // Fetch behavior types
  const { data: behaviorTypes, refetch: refetchBehaviorTypes } = useQuery({
    queryKey: ['behavior-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('behavior_types')
        .select('*')
        .eq('is_active', true)
        .order('category, name');
      
      if (error) throw error;
      console.log('AddBehaviorModal - Loaded behavior types:', data?.map(t => ({ name: t.name, name_el: t.name_el })));
      return data as BehaviorType[];
    },
    staleTime: 0, // Always fetch fresh
  });
  
  // Refetch behavior types when modal opens
  React.useEffect(() => {
    if (open) {
      console.log('AddBehaviorModal opened - refetching, language:', language);
      queryClient.invalidateQueries({ queryKey: ['behavior-types'] });
      refetchBehaviorTypes();
    }
  }, [open, refetchBehaviorTypes, queryClient, language]);

  // Fetch teams
  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('teams')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      return data as Team[];
    },
  });

  // Fetch guards based on selected team
  const { data: guards } = useQuery({
    queryKey: ['team-guards', selectedTeamId],
    queryFn: async () => {
      if (!selectedTeamId) return [];
      
      const { data, error } = await supabase
        .from('team_members')
        .select(`
          profile_id,
          profiles!inner (
            id,
            first_name,
            last_name,
            full_name
          )
        `)
        .eq('team_id', selectedTeamId);
      
      if (error) throw error;
      
      return data.map(member => ({
        id: member.profile_id,
        first_name: member.profiles.first_name || '',
        last_name: member.profiles.last_name || '',
        full_name: member.profiles.full_name || `${member.profiles.first_name || ''} ${member.profiles.last_name || ''}`.trim(),
      })) as Guard[];
    },
    enabled: !!selectedTeamId,
  });

  // Create behavior record mutation
  const createBehaviorMutation = useMutation({
    mutationFn: async (data: BehaviorFormData) => {
      // Get the selected behavior type to apply the correct score
      const behaviorType = behaviorTypes?.find(bt => bt.id === data.behavior_type_id);
      if (!behaviorType) {
        throw new Error('Behavior type not found');
      }

      const { data: result, error } = await supabase
        .from('behavior_records')
        .insert({
          guard_id: data.guard_id,
          team_id: data.team_id,
          behavior_type_id: data.behavior_type_id,
          incident_date: data.incident_date,
          description: data.description || null,
          evidence: data.evidence || null,
          score_applied: behaviorType.score_impact,
          recorded_by: (await supabase.auth.getUser()).data.user?.id || '',
          status: 'active',
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      toast({
        title: "Επιτυχής καταχώρηση",
        description: "Η συμπεριφορά καταχωρήθηκε επιτυχώς",
      });
      queryClient.invalidateQueries({ queryKey: ['behavior-records'] });
      reset();
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Σφάλμα",
        description: `Σφάλμα κατά την καταχώρηση: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: BehaviorFormData) => {
    createBehaviorMutation.mutate(data);
  };

  const selectedBehaviorType = behaviorTypes?.find(bt => bt.id === selectedBehaviorTypeId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Καταχώρηση Συμπεριφοράς
          </DialogTitle>
          <DialogDescription>
            Καταχωρήστε μια νέα συμπεριφορά για έναν φύλακα
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Team Selection */}
          <div className="space-y-2">
            <Label htmlFor="team_id">Ομάδα</Label>
            <Select
              onValueChange={(value) => {
                setValue('team_id', value);
                setValue('guard_id', ''); // Reset guard selection when team changes
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Επιλέξτε ομάδα..." />
              </SelectTrigger>
              <SelectContent>
                {teams?.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.team_id && (
              <p className="text-sm text-destructive">{errors.team_id.message}</p>
            )}
          </div>

          {/* Guard Selection */}
          <div className="space-y-2">
            <Label htmlFor="guard_id">Φύλακας</Label>
            <Select
              onValueChange={(value) => setValue('guard_id', value)}
              disabled={!selectedTeamId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Επιλέξτε φύλακα..." />
              </SelectTrigger>
              <SelectContent>
                {guards?.map((guard) => (
                  <SelectItem key={guard.id} value={guard.id}>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {guard.full_name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.guard_id && (
              <p className="text-sm text-destructive">{errors.guard_id.message}</p>
            )}
          </div>

          {/* Behavior Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="behavior_type_id">Τύπος Συμπεριφοράς</Label>
            <Select onValueChange={(value) => setValue('behavior_type_id', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Επιλέξτε τύπο συμπεριφοράς..." />
              </SelectTrigger>
              <SelectContent>
                {behaviorTypes?.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <div className="flex items-center gap-2">
                      {type.category === 'positive' ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      )}
                      <span>{getLocalizedName(type)}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        type.category === 'positive' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {type.score_impact > 0 ? '+' : ''}{type.score_impact} πόντοι
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.behavior_type_id && (
              <p className="text-sm text-destructive">{errors.behavior_type_id.message}</p>
            )}
          </div>

          {/* Selected Behavior Type Info */}
          {selectedBehaviorType && (
            <div className={`p-3 rounded-lg border ${
              selectedBehaviorType.category === 'positive' 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                {selectedBehaviorType.category === 'positive' ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                )}
                <span className="font-medium">{getLocalizedName(selectedBehaviorType)}</span>
              </div>
              {getLocalizedDescription(selectedBehaviorType) && (
                <p className="text-sm text-muted-foreground mb-2">
                  {getLocalizedDescription(selectedBehaviorType)}
                </p>
              )}
              <p className="text-sm font-medium">
                Επίδραση στην αξιολόγηση: {selectedBehaviorType.score_impact > 0 ? '+' : ''}{selectedBehaviorType.score_impact} πόντοι
              </p>
            </div>
          )}

          {/* Incident Date */}
          <div className="space-y-2">
            <Label htmlFor="incident_date">Ημερομηνία Περιστατικού</Label>
            <Input
              id="incident_date"
              type="date"
              {...register('incident_date')}
            />
            {errors.incident_date && (
              <p className="text-sm text-destructive">{errors.incident_date.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Περιγραφή</Label>
            <Textarea
              id="description"
              placeholder="Περιγράψτε το περιστατικό..."
              rows={3}
              {...register('description')}
            />
          </div>

          {/* Evidence */}
          <div className="space-y-2">
            <Label htmlFor="evidence">Αποδεικτικά Στοιχεία</Label>
            <Textarea
              id="evidence"
              placeholder="Αποδεικτικά στοιχεία ή σχόλια..."
              rows={2}
              {...register('evidence')}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Ακύρωση
            </Button>
            <Button
              type="submit"
              disabled={createBehaviorMutation.isPending}
            >
              {createBehaviorMutation.isPending ? 'Καταχώρηση...' : 'Καταχώρηση'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};